import {
  SecretScope,
  SecretType,
  SecretMode,
  SecretResponse,
  SecretsListResponse,
  CreateSecretDto,
  UpdateSecretDto,
  ImportEnvDto,
  LinkSecretsDto,
  UnlinkSecretDto,
  ImportEnvResult,
} from '@optidev/shared-types';

import { apiClient, createAuthHeaders } from '../api-client';

export const secretsApi = {
  /**
   * Get all secrets with optional filtering
   */
  async getAll(
    token: string,
    scope: SecretScope,
    projectId?: string,
    search?: string,
    type?: SecretType,
  ): Promise<SecretsListResponse> {
    const params = new URLSearchParams();
    params.append('scope', scope);
    if (projectId) params.append('projectId', projectId);
    if (search) params.append('search', search);
    if (type) params.append('type', type);

    const response = await apiClient.get(`/api/secrets?${params.toString()}`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Get a single secret by ID
   */
  async getOne(token: string, id: string): Promise<SecretResponse> {
    const response = await apiClient.get(`/api/secrets/${id}`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Create a new secret
   */
  async create(token: string, dto: CreateSecretDto): Promise<SecretResponse> {
    const response = await apiClient.post('/api/secrets', dto, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Update an existing secret
   */
  async update(token: string, id: string, dto: UpdateSecretDto): Promise<SecretResponse> {
    const response = await apiClient.put(`/api/secrets/${id}`, dto, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Delete a secret
   */
  async delete(token: string, id: string): Promise<void> {
    await apiClient.delete(`/api/secrets/${id}`, {
      headers: createAuthHeaders(token),
    });
  },

  /**
   * Import secrets from .env file content
   */
  async importEnv(token: string, dto: ImportEnvDto): Promise<ImportEnvResult> {
    const response = await apiClient.post('/api/secrets/import-env', dto, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Toggle secret mode (lite/power)
   */
  async toggleMode(token: string, id: string, mode: SecretMode): Promise<SecretResponse> {
    const response = await apiClient.post(
      `/api/secrets/${id}/toggle-mode`,
      { mode },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Link account secrets to a project
   */
  async link(token: string, dto: LinkSecretsDto): Promise<{ created: number; skipped: number }> {
    const response = await apiClient.post('/api/secrets/link', dto, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Unlink a secret from a project
   */
  async unlink(token: string, dto: UnlinkSecretDto): Promise<{ success: boolean }> {
    const response = await apiClient.post('/api/secrets/unlink', dto, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },
};
